/* eslint-disable no-restricted-globals */
/**
 * The `app-gap` elements embeds a javascript application inside an IFrame
 *  attributes:
 *   - module: the url of the module to load
 *   - component: the tagname of the element to render
 *   - options: will be passed to the child component, usually a JSON string
 *   - endpoint: the URL of the backend service the component communicates with
 *   - jwt-token: bearer token the UI component should use to communicate with its backend
 *   - prefix: the URL prefix tha app should manage its own navigation
 *   - theme: the selected theme the component should adhere to
 *   - scripts: URLs of scripts separated by commas. These scripts will be appended to the inside iframe document
 *   - styles: the given string will be appended to the inside iframe document's head as a style tag
 *   - language: string representation of the chosen language
 *   - standalone: optional flag to let the app know it's the only one
 */
import { escape } from 'html-escaper';
import { v4 as uuidv4 } from 'uuid';
export class AppGap extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.appGapId = uuidv4();
        this.waitForComponent = new Promise(resolve => {
            this.componentReady = resolve;
            return this.componentReady;
        });
        this.waitForAppLoad = new Promise(resolve => {
            this.appReady = resolve;
            return this.appReady;
        });
    }
    static get observedAttributes() {
        return [
            'options',
            'endpoint',
            'jwt-token',
            'navigation-route',
            'theme',
            'module',
            'scripts',
            'styles',
            'language',
            'prefix',
            'fragments',
            'standalone',
            'features',
        ];
    }
    get module() {
        return this.getAttribute('module');
    }
    get component() {
        return encodeURIComponent(this.getAttribute('component'));
    }
    get scripts() {
        return this.getAttribute('extra-scripts');
    }
    get elementPrefix() {
        const prefix = this.getAttribute('prefix') || '';
        if (prefix.startsWith('/')) {
            return encodeURIComponent(prefix.substring(1));
        }
        return encodeURIComponent(prefix);
    }
    get styles() {
        return this.getAttribute('styles');
    }
    get options() {
        return JSON.parse(this.getAttribute('options'));
    }
    connectedCallback() {
        if (this.module && this.component) {
            window.addEventListener('message', this.onMessage);
            window.addEventListener('reload-app-iframe', event => {
                if (event.detail.id === this.getAttribute('id'))
                    this.recreateFrame();
            });
            this.addEventListener('synapse-frame-event', this.trickleEvent);
            this.shadowRoot.appendChild(this.createFrame(this.module, this.component, this.scripts, this.styles));
        }
    }
    disconnectedCallback() {
        window.removeEventListener('message', this.onMessage);
    }
    attributeChangedCallback(name, oldVal, newVal) {
        this.waitForComponent.then(() => this.changeInnerAttribute(name, newVal));
    }
    changeInnerAttribute(name, value) {
        // eslint-disable-next-line no-unused-expressions
        this.shadowRoot.querySelector('iframe')?.contentDocument.querySelector(this.component)?.setAttribute(name, value);
    }
    /**
     *
     * @param {MessageEvent} event
     * @returns
     */
    onMessage = event => {
        /**
         * top is used because of iFrame inside iFrame
         * e.g. app gap renders app fragments
         */
        if (event.origin !== top.location.origin) {
            console.warn(`Rejected due to mismatching origin. Actual: ${event.origin}, Expected: ${top.location.origin}`);
            return;
        }
        if (event.data.appGapId !== this.appGapId) {
            console.log(`Cancelling events due to mismatching appGapId`);
            return;
        }
        if (this.hasAttribute('hidden')) {
            return;
        }
        this.dispatchEvent(new CustomEvent('synapse-app-event', { detail: { ...event.data.eventDetails }, bubbles: true, capture: true }));
        if (event.data?.eventDetails?.type === 'module-loaded') {
            this.componentReady();
        }
        if (event.data?.eventDetails?.type === 'app-loaded') {
            this.appReady();
        }
        if (event.data?.eventDetails?.type === 'module-load-failed') {
            // Timeout is needed for Sentry to finish the initialization before the error
            setTimeout(() => {
                throw Error(`App failed to load: ${event.data?.eventDetails?.component}`);
            }, 3000);
        }
    };
    /**
     *
     * @param {Event} event
     */
    trickleEvent = async (event) => {
        await this.waitForComponent;
        if (this.options?.waitForAppLoad) {
            await this.waitForAppLoad;
        }
        this.shadowRoot
            .querySelector('iframe')
            ?.contentDocument?.querySelector(this.component)
            .dispatchEvent(new CustomEvent(event.type, { detail: event.detail, bubbles: true, composed: true, }));
    };
    recreateFrame() {
        this.shadowRoot.innerHTML = null;
        this.shadowRoot.appendChild(this.createFrame(this.module, this.component, this.scripts, this.styles));
    }
    createFrame(module, component, scripts = null, styles = null) {
        const element = document.createElement('iframe');
        const frameDocument = /* html */ `
        <style>
          html, body {
            height: 100%;
            margin: 0px;
            font-family: ${window.getComputedStyle(this).fontFamily};
          }
        </style>
        ${styles
            ? `<style class="custom-styles">
          ${styles}
        </style>`
            : ''}
        <body>
          <script type="module" src="${module}" onload="scriptLoaded()" onError="scriptFailed('${component}')"></script>
          <${component}
            ${this.spreadAttributes()}
          ></${component}>

          <script>
            window.fragments = ${this.getAttribute('fragments')};

            function bubbleEvent(e) {
              /**
               * top is used because of iFrame inside iFrame
               * e.g. app gap renders app fragments
               */
              parent.postMessage({appGapId: "${this.appGapId}", eventDetails: e.detail}, top.location.origin);
            }

            function scriptLoaded(e) {
              bubbleEvent({detail: { type: 'module-loaded' }});
            }

            function scriptFailed(component) {
              bubbleEvent({detail: { type: 'module-load-failed', component }});
            }

            window.addEventListener('synapse-app-event', bubbleEvent);
            window.location.href = "about:srcdoc#/${this.elementPrefix}";
          </script>

          ${scripts
            ? scripts.split(',').map(script => `
            <script class="synapse-extra-script" type="module" src="${script}"></script>
          `)
            : ''}
        </body>
      `;
        element.srcdoc = frameDocument;
        element.style = `
        display: block;
        width: 100%;
        height: 100%;
        border: 0px;
      `;
        return element;
    }
    spreadAttributes() {
        return AppGap.observedAttributes
            .map(attribute => {
            const value = this.getAttribute(attribute);
            if (value === null) {
                return '';
            }
            else if (value === '') {
                return attribute;
            }
            return `${attribute}='${escape(value)}'`;
        })
            .join(' ');
    }
}
window.customElements.define('app-gap', AppGap);
